import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import PawPrint from "@/components/PawPrint";

export default function AdminPage() {
  const { user, API } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notifTitle, setNotifTitle] = useState("");
  const [notifBody, setNotifBody] = useState("");
  const [notifSending, setNotifSending] = useState(false);
  const [notifResult, setNotifResult] = useState("");

  useEffect(() => {
    if (!user?.is_admin) {
      navigate("/");
      return;
    }
    loadData();
  }, [user]);

  async function loadData() {
    try {
      const [statsRes, usersRes, fbRes] = await Promise.all([
        API.get("/api/admin/stats"),
        API.get("/api/admin/users"),
        API.get("/api/feedback/all").catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setFeedback(fbRes.data || []);
    } catch (err) {
      console.error("Admin load error:", err);
    } finally {
      setLoading(false);
    }
  }

  async function toggleAdmin(userId) {
    try {
      const { data } = await API.post(`/api/admin/users/${userId}/toggle-admin`);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_admin: data.is_admin } : u));
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur lors de la modification");
    }
  }

  async function approveUser(userId) {
    try {
      await API.post(`/api/admin/users/${userId}/approve`);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_approved: true } : u));
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur lors de l'approbation");
    }
  }

  async function rejectUser(userId, userName) {
    if (!window.confirm(`Refuser et supprimer la demande de ${userName} ?`)) return;
    try {
      await API.post(`/api/admin/users/${userId}/reject`);
      setUsers(prev => prev.filter(u => u.id !== userId));
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur lors du refus");
    }
  }

  async function deleteUser(userId, userName) {
    if (!window.confirm(`Supprimer DEFINITIVEMENT le compte de ${userName} et toutes ses donnees (animaux, consultations, messages) ? Cette action est irreversible.`)) return;
    try {
      await API.delete(`/api/admin/users/${userId}`);
      setUsers(prev => prev.filter(u => u.id !== userId));
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur lors de la suppression");
    }
  }

  const pendingUsers = users.filter(u => u.is_approved === false);
  const approvedUsers = users.filter(u => u.is_approved !== false);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FFF8F2] via-[#FFF5F9] to-[#F5F0FF] flex items-center justify-center">
        <div className="animate-bounce-in">
          <PawPrint className="w-12 h-12 text-pawcare-orange" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFF8F2] via-[#FFF5F9] to-[#F5F0FF] pb-24">
      {/* Header */}
      <div className="px-5 pb-6" style={{ paddingTop: "max(56px, calc(env(safe-area-inset-top, 0px) + 40px))" }}>
        <h1 className="text-2xl font-extrabold text-dark font-display">
          Administration
        </h1>
        <p className="text-muted text-sm mt-1">Suivi des couts API Anthropic</p>
      </div>

      {/* Stats cards */}
      <div className="px-5 grid grid-cols-3 gap-3">
        <StatCard
          label="Aujourd'hui"
          cost={stats?.today_cost}
          requests={stats?.today_requests}
          color="#FFB347"
        />
        <StatCard
          label="Ce mois"
          cost={stats?.month_cost}
          requests={stats?.month_requests}
          color="#FF7055"
        />
        <StatCard
          label="Total"
          cost={stats?.total_cost}
          requests={stats?.total_requests}
          color="#FF5C8D"
        />
      </div>

      {/* Token summary */}
      <div className="px-5 mt-4">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-sm border border-white/50">
          <div className="flex justify-between text-sm">
            <span className="text-muted">Tokens input total</span>
            <span className="font-bold text-dark">{(stats?.total_input_tokens || 0).toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-sm mt-2">
            <span className="text-muted">Tokens output total</span>
            <span className="font-bold text-dark">{(stats?.total_output_tokens || 0).toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-sm mt-2">
            <span className="text-muted">Modele</span>
            <span className="font-semibold text-pawcare-orange">Claude Sonnet 4</span>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div className="px-5 mt-6">
        <h2 className="text-lg font-bold text-dark mb-3">Couts par jour (30j)</h2>
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-sm border border-white/50">
          {stats?.daily?.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={stats.daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: "#AAA" }}
                  tickFormatter={(d) => d.slice(5)}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: "#AAA" }}
                  tickFormatter={(v) => `$${v.toFixed(2)}`}
                />
                <Tooltip
                  formatter={(value, name) => {
                    if (name === "cost") return [`$${value.toFixed(4)}`, "Cout"];
                    return [value, "Requetes"];
                  }}
                  labelFormatter={(label) => `Date: ${label}`}
                  contentStyle={{ borderRadius: 12, border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}
                />
                <Bar dataKey="cost" fill="#FFB347" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-center text-muted py-8">Aucune donnee pour le moment</p>
          )}
        </div>
      </div>

      {/* Feedback section */}
      <div className="px-5 mt-6">
        <h2 className="text-lg font-bold text-dark mb-3">Avis utilisateurs ({feedback.length})</h2>
        {feedback.length > 0 && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-sm border border-white/50 mb-2">
            <div className="flex items-center gap-3 mb-3">
              <div className="text-3xl">
                {feedback.length > 0
                  ? (feedback.reduce((s, f) => s + f.rating, 0) / feedback.length).toFixed(1)
                  : "—"}
              </div>
              <div>
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map(s => (
                    <span key={s} className="text-lg" style={{ filter: s <= Math.round(feedback.reduce((a, f) => a + f.rating, 0) / feedback.length) ? "none" : "grayscale(1) opacity(0.25)" }}>
                      ⭐
                    </span>
                  ))}
                </div>
                <div className="text-xs text-muted font-semibold">{feedback.length} avis</div>
              </div>
            </div>
          </div>
        )}
        <div className="space-y-2.5">
          {feedback.map((f, i) => (
            <div key={i} className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-sm border border-white/50">
              <div className="flex items-center justify-between mb-1.5">
                <div className="font-semibold text-sm text-dark">{f.user_name || "Anonyme"}</div>
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map(s => (
                    <span key={s} className="text-sm" style={{ filter: s <= f.rating ? "none" : "grayscale(1) opacity(0.2)" }}>⭐</span>
                  ))}
                </div>
              </div>
              {f.comment && (
                <p className="text-[13px] text-muted leading-relaxed">{f.comment}</p>
              )}
              {f.created_at && (
                <div className="text-[11px] text-gray-400 mt-1.5">{new Date(f.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}</div>
              )}
            </div>
          ))}
          {feedback.length === 0 && (
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 text-center shadow-sm border border-white/50">
              <div className="text-3xl mb-2">🧪</div>
              <p className="text-muted text-sm">Aucun avis pour le moment</p>
            </div>
          )}
        </div>
      </div>

      {/* Notifications */}
      <div className="px-5 mt-6">
        <h2 className="text-lg font-bold text-dark mb-3">Notifications push</h2>
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-sm border border-white/50">
          <input
            value={notifTitle}
            onChange={e => setNotifTitle(e.target.value)}
            placeholder="Titre de la notification"
            className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm font-semibold mb-2 outline-none focus:border-pawcare-orange/50"
          />
          <textarea
            value={notifBody}
            onChange={e => setNotifBody(e.target.value)}
            placeholder="Message..."
            rows={2}
            className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm font-semibold mb-3 outline-none focus:border-pawcare-orange/50 resize-none"
          />
          <div className="flex gap-2">
            <button
              onClick={async () => {
                if (!notifTitle.trim()) return;
                setNotifSending(true);
                try {
                  const { data } = await API.post("/api/notifications/broadcast", { title: notifTitle, body: notifBody });
                  setNotifResult(`Envoye a ${data.sent} appareil(s)`);
                  setNotifTitle("");
                  setNotifBody("");
                } catch (err) {
                  setNotifResult("Erreur: " + (err.response?.data?.detail || err.message));
                }
                setNotifSending(false);
                setTimeout(() => setNotifResult(""), 4000);
              }}
              disabled={notifSending || !notifTitle.trim()}
              className="flex-1 py-2.5 rounded-xl border-none cursor-pointer font-bold text-sm text-white disabled:opacity-50"
              style={{ background: "linear-gradient(135deg, #FFB347, #FF5C8D)" }}
            >
              {notifSending ? "Envoi..." : "Envoyer a tous"}
            </button>
            <button
              onClick={async () => {
                setNotifSending(true);
                try {
                  const { data } = await API.post("/api/notifications/check-reminders");
                  setNotifResult(`${data.reminders_sent} rappel(s) vaccin envoye(s)`);
                } catch (err) {
                  setNotifResult("Erreur: " + (err.response?.data?.detail || err.message));
                }
                setNotifSending(false);
                setTimeout(() => setNotifResult(""), 4000);
              }}
              disabled={notifSending}
              className="py-2.5 px-3 rounded-xl border border-gray-200 cursor-pointer font-bold text-sm text-dark bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              💉 Rappels vaccins
            </button>
            <button
              onClick={async () => {
                setNotifSending(true);
                try {
                  const { data } = await API.post("/api/notifications/weekly-recap");
                  setNotifResult(`${data.recaps_sent} recap(s) envoye(s)`);
                } catch (err) {
                  setNotifResult("Erreur: " + (err.response?.data?.detail || err.message));
                }
                setNotifSending(false);
                setTimeout(() => setNotifResult(""), 4000);
              }}
              disabled={notifSending}
              className="py-2.5 px-3 rounded-xl border border-gray-200 cursor-pointer font-bold text-sm text-dark bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              📅 Recap hebdo
            </button>
            <button
              onClick={async () => {
                setNotifSending(true);
                try {
                  const { data } = await API.post("/api/notifications/missions-reminder");
                  setNotifResult(`${data.reminders_sent} rappel(s) missions envoye(s)`);
                } catch (err) {
                  setNotifResult("Erreur: " + (err.response?.data?.detail || err.message));
                }
                setNotifSending(false);
                setTimeout(() => setNotifResult(""), 4000);
              }}
              disabled={notifSending}
              className="py-2.5 px-3 rounded-xl border border-gray-200 cursor-pointer font-bold text-sm text-dark bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              ✨ Rappel missions
            </button>
          </div>
          {notifResult && (
            <div className="mt-2 text-center text-sm font-semibold text-green-600">{notifResult}</div>
          )}
        </div>
      </div>

      {/* Pending registrations */}
      <div className="px-5 mt-6">
        <h2 className="text-lg font-bold text-dark mb-3">
          Inscriptions en attente
          {pendingUsers.length > 0 && (
            <span className="ml-2 inline-block bg-pawcare-orange text-white rounded-full text-xs px-2 py-0.5 align-middle">
              {pendingUsers.length}
            </span>
          )}
        </h2>
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm border border-white/50 overflow-hidden">
          {pendingUsers.length === 0 ? (
            <div className="p-6 text-center text-muted text-sm">
              Aucune demande en attente
            </div>
          ) : (
            <ul className="divide-y divide-gray-50">
              {pendingUsers.map(u => (
                <li key={u.id} className="px-4 py-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-dark truncate">{u.name}</div>
                    <div className="text-xs text-muted truncate">{u.email}</div>
                    {u.created_at && (
                      <div className="text-[11px] text-gray-400 mt-0.5">
                        Demande {new Date(u.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" })}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => approveUser(u.id)}
                    className="px-3 py-1.5 rounded-xl bg-green-50 text-green-600 font-bold text-xs border-none cursor-pointer hover:bg-green-100"
                  >
                    Approuver
                  </button>
                  <button
                    onClick={() => rejectUser(u.id, u.name)}
                    className="px-3 py-1.5 rounded-xl bg-red-50 text-red-500 font-bold text-xs border-none cursor-pointer hover:bg-red-100"
                  >
                    Refuser
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Users table */}
      <div className="px-5 mt-6">
        <h2 className="text-lg font-bold text-dark mb-3">Utilisateurs</h2>
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm border border-white/50 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left px-4 py-3 text-muted font-semibold">Nom</th>
                  <th className="text-right px-4 py-3 text-muted font-semibold">Consult.</th>
                  <th className="text-right px-4 py-3 text-muted font-semibold">Cout API</th>
                  <th className="text-right px-4 py-3 text-muted font-semibold">Role</th>
                  <th className="text-right px-4 py-3 text-muted font-semibold">Action</th>
                </tr>
              </thead>
              <tbody>
                {approvedUsers.map((u) => (
                  <tr key={u.id} className="border-b border-gray-50 last:border-0">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-dark">{u.name}</div>
                      <div className="text-xs text-muted">{u.email}</div>
                    </td>
                    <td className="text-right px-4 py-3 font-medium text-dark">{u.consultations}</td>
                    <td className="text-right px-4 py-3 font-bold text-pawcare-orange">${(u.api_cost ?? 0).toFixed(4)}</td>
                    <td className="text-right px-4 py-3">
                      {u.id === user.id ? (
                        <span className="inline-block px-2.5 py-1 rounded-full text-[11px] font-bold bg-pawcare-orange/15 text-pawcare-orange-dark">
                          Admin (vous)
                        </span>
                      ) : (
                        <button
                          onClick={() => toggleAdmin(u.id)}
                          className={`inline-block px-2.5 py-1 rounded-full text-[11px] font-bold border-none cursor-pointer transition-all duration-200 ${
                            u.is_admin
                              ? "bg-red-50 text-red-500 hover:bg-red-100"
                              : "bg-green-50 text-green-600 hover:bg-green-100"
                          }`}
                        >
                          {u.is_admin ? "Retirer admin" : "Promouvoir admin"}
                        </button>
                      )}
                    </td>
                    <td className="text-right px-4 py-3">
                      {u.id !== user.id && (
                        <button
                          onClick={() => deleteUser(u.id, u.name)}
                          title="Supprimer le compte et toutes ses donnees"
                          className="px-2.5 py-1 rounded-full text-[11px] font-bold border-none cursor-pointer bg-red-50 text-red-500 hover:bg-red-100 transition-colors"
                        >
                          🗑 Supprimer
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {approvedUsers.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center py-8 text-muted">Aucun utilisateur</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, cost = 0, requests = 0, color }) {
  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-3 shadow-sm border border-white/50 text-center">
      <p className="text-xs text-muted mb-1">{label}</p>
      <p className="text-lg font-extrabold" style={{ color }}>
        ${(cost ?? 0).toFixed(4)}
      </p>
      <p className="text-xs text-muted mt-1">{requests} req.</p>
    </div>
  );
}
